// Helpers for the Network Editor demo (Ithaca): LOS/VC coloring, metric joins,
// and the /api/demo/* data fetchers. Pure + fetch only — deck.gl layer objects
// are built in MapContainer to match the existing per-feature layer pattern.

import { isLiveSimulation } from '../config';

const DEMO_API = '/api/demo';

// --- LOS colouring (absolute V/C) -----------------------------------------
// Thresholds match PPSforSUMO: A <0.6, B <0.7, C <0.8, D <0.9, E <1.0, F >=1.0.
// Palette is RdYlGn reversed. `range`/`desc` live here so the legend cannot drift.
// Wording describes V/C, not the textbook density/delay LOS definition.
const LOS_STOPS = [
  { grade: 'A', max: 0.6, color: [26, 152, 80], range: '< 0.60', desc: 'Free flow' },
  { grade: 'B', max: 0.7, color: [145, 207, 96], range: '0.60 – 0.70', desc: 'Stable flow' },
  { grade: 'C', max: 0.8, color: [217, 239, 139], range: '0.70 – 0.80', desc: 'Light congestion' },
  { grade: 'D', max: 0.9, color: [254, 224, 139], range: '0.80 – 0.90', desc: 'Approaching capacity' },
  { grade: 'E', max: 1.0, color: [252, 141, 89], range: '0.90 – 1.00', desc: 'At capacity' },
  { grade: 'F', max: Infinity, color: [215, 48, 39], range: '≥ 1.00', desc: 'Over capacity' },
];

const NO_DATA_COLOR = [120, 120, 120, 90]; // edge exists but carries no modeled traffic

export function losGrade(vc) {
  for (const s of LOS_STOPS) if (vc < s.max) return s.grade;
  return 'F';
}

export function losColor(vc, alpha = 220) {
  if (vc == null) return NO_DATA_COLOR;
  for (const s of LOS_STOPS) if (vc < s.max) return [...s.color, alpha];
  return [...LOS_STOPS[LOS_STOPS.length - 1].color, alpha];
}

export const LOS_LEGEND = LOS_STOPS.map((s) => ({
  grade: s.grade, color: s.color, range: s.range, desc: s.desc,
}));

// --- Delta colouring (vs compared_against) --------------------------------
// Diverging: green = relieved, red = worsened. One table for colour and legend.
// `exclusive` matters: the relief bands are strict, so exactly -0.05 is "minimal change".
// The grey band reaches 0.05 because the pre-computed maps only fetch roads that moved
// 0.02 or more (PREBAKED_MIN_DELTA): a grey band inside that cutoff could never be drawn.
const VC_DELTA_STOPS = [
  { max: -0.1,     exclusive: true, color: [0, 104, 55],    label: 'Relieved (\u0394V/C < -0.1)' },
  { max: -0.05,    exclusive: true, color: [166, 217, 106], label: 'Slightly relieved (-0.1 to -0.05)' },
  { max: 0.05,     dim: true,       color: [150, 150, 150], label: 'Minimal change (\u00b10.05)' },
  { max: 0.1,                       color: [253, 174, 97],  label: 'Slightly worsened (0.05 to 0.1)' },
  { max: Infinity,                  color: [165, 0, 38],    label: 'Worsened (\u0394V/C > 0.1)' },
];

export const VC_DELTA_LEGEND = VC_DELTA_STOPS.map((s) => ({ label: s.label, color: s.color }));

// Trip flows (flowmap.gl). An array, not a named scheme: flowmap.gl reverses a named
// sequential scheme in dark mode. Ends are moved off real magma — the top stop also
// paints the zone circles, the bottom is a cold indigo because fading multiplies HCL lightness.
export const FLOW_SCHEME = [
  '#2a1a4a', '#432272', '#6a2585', '#9a2f80', '#c8407a',
  '#ee5f5e', '#fd9668', '#fed08a', '#fbeb4a',
];

export function vcDeltaColor(delta, alpha = 220) {
  if (delta == null) return NO_DATA_COLOR;
  for (const s of VC_DELTA_STOPS) {
    if (s.exclusive ? delta < s.max : delta <= s.max) {
      return [...s.color, s.dim ? 160 : alpha];
    }
  }
  return [...VC_DELTA_STOPS[VC_DELTA_STOPS.length - 1].color, alpha];
}

// --- speed units ----------------------------------------------------------
// The wire format is km/h everywhere (edges.geojson, attrs.<id>.speed_kmh, the
// netconvert patch). Only the UI is imperial — a converted value is never stored.
// The exact factor matters: 1.6 drifts a 65 mph limit by ~0.6 km/h.
export const KMH_PER_MPH = 1.609344;

// Whole mph. Rounding here is what lets `dirty` compare in mph space (see RoadEditor);
// a rounded mph back to km/h is lossy.
export const kmhToMph = (kmh) => Math.round((kmh || 0) / KMH_PER_MPH);

// Back to the wire. Two decimals is well inside netconvert's resolution and
// keeps the JSON from carrying float noise.
export const mphToKmh = (mph) => Number(((mph || 0) * KMH_PER_MPH).toFixed(2));

// Unrounded, for realised speeds rather than posted limits. Call sites pick decimals.
export const kmhToMphExact = (kmh) => (kmh || 0) / KMH_PER_MPH;

// Wider stroke for edges that carry more traffic, so busy roads read first.
export function widthForCount(count) {
  if (!count) return 1;
  if (count > 2000) return 5;
  if (count > 1000) return 4;
  if (count > 300) return 3;
  return 2;
}

// --- data fetchers ------------------------------------------------------------
const getJson = (path) =>
  fetch(`${DEMO_API}${path}`).then((r) => {
    if (!r.ok) throw new Error(`${path} -> ${r.status}`);
    return r.json();
  });

// Every metro-bound request names its metro: the edit service holds several at
// once and answers 400 when a request that could mean any of them names none.
const q = (cbsa) => (cbsa ? `?cbsa=${encodeURIComponent(cbsa)}` : '');

export const fetchManifest = () => getJson('/manifest');
// Resolves to null rather than throwing when the pack has no safety baseline.
export const fetchSafetyBaseline = () =>
  getJson('/safety/baseline').then((d) => d?.safety || null).catch(() => null);
export const fetchEdges = (cbsa) => getJson(`/network/edges${q(cbsa)}`);
export const fetchTls = (cbsa) => getJson(`/network/tls${q(cbsa)}`);

/** Metros the edit service can hold, with an edge estimate and their state. */
export const fetchMetros = () =>
  getJson('/metros').then((d) => d.metros || []).catch(() => []);

/** Ask the service to hold a metro. Answers at once; poll metroHealth for the rest. */
export const loadMetro = (cbsa) =>
  fetch(`${DEMO_API}/load`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ cbsa: String(cbsa), run_id: 'latest' }),
  }).then(async (r) => {
    const body = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(body.error || `load ${r.status}`);
    return body;
  });

/** One metro's state: absent | loading | warm | baseline_ready | failed, plus
 *  `publish` — whether its V/C layer has reached the serving layer
 *  (pending | done | skipped | failed), which is a second wait after the
 *  simulation is ready. Null metro means the service could not be asked. */
export const metroHealth = (cbsa) =>
  getJson('/health').then((h) => ({
    metro: h?.simulation?.metros?.[String(cbsa)] || null,
    publish: h?.publish?.[String(cbsa)] || null,
  }));

/** Centre of a network, for flying to a metro. Junctions, not the full bbox:
 *  a CBSA bbox centre lands in farmland tens of km from the city. */
export function centerOfJunctions(tls) {
  const pts = (tls || []).filter((j) => Number.isFinite(j.lon) && Number.isFinite(j.lat));
  if (!pts.length) return null;
  const lons = pts.map((j) => j.lon).sort((a, b) => a - b);
  const lats = pts.map((j) => j.lat).sort((a, b) => a - b);
  const mid = (a) => a[Math.floor(a.length / 2)];
  return { longitude: mid(lons), latitude: mid(lats), zoom: 11.5 };
}
export const fetchMetrics = (id) => getJson(`/scenario/${id}/metrics`);
export const fetchComparison = (id) => getJson(`/scenario/${id}/comparison`);
export const fetchEdits = (id) => getJson(`/scenario/${id}/edits`);

// Submit staged edits and run the real SUMO chain (netconvert -> duarouter ->
// metrics) on them. Takes ~30 s; resolves to {edges, summary} in the same shape
// as a baked scenario's metrics.json.
export function runSimulation(edits) {
  return fetch(`${DEMO_API}/run`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(edits),
  }).then(async (r) => {
    if (!r.ok) {
      const body = await r.json().catch(() => ({}));
      throw new Error(body.error || `simulation failed (${r.status})`);
    }
    return r.json();
  });
}

// --- Custom demand --------------------------------------------------------
// Both halves are checked by the service: only it knows which zone ids the network has.

/** Header -> od_format. No sniffing fallback: an H3 cell and a TAZ id are both opaque
 *  strings, so a mislabelled file silently matches the wrong zone set. */
const OD_HEADERS = {
  'from_h3,to_h3,count': 'h3',
  'from_taz,to_taz,count': 'taz',
};

/** Parse an OD CSV: three columns, one header row. Hand-rolled — the grammar has no
 *  quoting or embedded newlines. Errors carry the user's own line numbers; any
 *  structural error rejects the whole file. */
export function parseOdCsv(text) {
  const errors = [];
  const rows = [];
  const lines = String(text || '').split(/\r?\n/);
  let sawHeader = false;
  let odFormat = null;

  for (let i = 0; i < lines.length; i++) {
    const raw = lines[i];
    const lineNo = i + 1;
    if (!raw.trim()) continue;
    const cells = raw.split(',').map((c) => c.trim());

    if (!sawHeader) {
      sawHeader = true;
      odFormat = OD_HEADERS[cells.join(',').toLowerCase()];
      if (!odFormat) {
        return {
          ok: false,
          rows: [],
          odFormat: null,
          errors: [`Line ${lineNo}: header must be "from_h3,to_h3,count" or `
            + `"from_taz,to_taz,count" — found "${raw.trim().slice(0, 60)}"`],
        };
      }
      continue;
    }

    if (cells.length !== 3) {
      errors.push(`Line ${lineNo}: expected 3 columns (from, to, count), found ${cells.length}`);
      continue;
    }
    const [from, to, countText] = cells;
    if (!from || !to) {
      errors.push(`Line ${lineNo}: origin and destination cannot be blank`);
      continue;
    }
    const count = Number(countText);
    if (countText === '' || !Number.isFinite(count)) {
      errors.push(`Line ${lineNo}: count "${countText}" is not a number`);
      continue;
    }
    if (count < 0) {
      errors.push(`Line ${lineNo}: count ${countText} is negative`);
      continue;
    }
    rows.push({ from, to, count });
  }

  if (!sawHeader) errors.push('That file is empty.');
  else if (!rows.length && !errors.length) errors.push('That file has a header but no rows.');

  return { ok: errors.length === 0 && rows.length > 0, errors, rows, odFormat };
}

/** The vehicle types a mix can use and the metro's own mix, or null when the
 *  service cannot answer (the editor then shows nothing to mix). */
export function fetchFleetTypes(cbsa) {
  return fetch(`${DEMO_API}/fleet/types${q(cbsa)}`)
    .then((r) => (r.ok ? r.json() : null))
    .catch(() => null);
}

/** Read an uploaded <tazs> document. Structure only; the service decides usability.
 *  The text is passed VERBATIM — the service canonicalises and hashes it itself. */
export function parseTazXml(text) {
  const raw = String(text || '').trim();
  if (!raw) return { ok: false, tazXml: null, count: 0, errors: ['That file is empty.'] };
  let doc;
  try {
    doc = new DOMParser().parseFromString(raw, 'application/xml');
  } catch (e) {
    return { ok: false, tazXml: null, count: 0, errors: ['That file could not be parsed as XML.'] };
  }
  // DOMParser reports failure as a <parsererror> element rather than throwing.
  if (doc.querySelector('parsererror')) {
    return {
      ok: false,
      tazXml: null,
      count: 0,
      errors: ['That file is not valid XML. Check for an unclosed tag.'],
    };
  }
  const root = doc.documentElement;
  if (!root || root.nodeName !== 'tazs') {
    return {
      ok: false,
      tazXml: null,
      count: 0,
      errors: [`The root element is <${root ? root.nodeName : '?'}> — it must be <tazs>.`],
    };
  }
  const zones = root.getElementsByTagName('taz');
  if (!zones.length) {
    return { ok: false, tazXml: null, count: 0, errors: ['That file has no <taz> elements.'] };
  }
  return { ok: true, tazXml: raw, count: zones.length, errors: [] };
}

/** Dry-run a demand override. Resolves to the service's report (which may carry
 *  ok:false — a rejection with reasons is data). Rejects only on transport failure;
 *  the error carries `unreachable`. */
export async function validateDemand(demand, cbsa) {
  let r;
  try {
    r = await fetch(`${DEMO_API}/demand/validate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(cbsa ? { ...demand, cbsa } : demand),
    });
  } catch (e) {
    throw Object.assign(new Error('Simulation service is not reachable.'),
      { unreachable: true });
  }
  const body = await r.json().catch(() => ({}));
  if (r.status === 502 || r.status === 504) {
    throw Object.assign(new Error(body.error || 'Simulation service is not reachable.'),
      { unreachable: true });
  }
  if (body && body.ok === false && Array.isArray(body.errors)) return body;
  if (!r.ok) throw new Error(body.error || `Demand check failed (${r.status})`);
  return body;
}

// --- Trip flows ---------------------------------------------------------------

export const fetchBakedDemand = (cbsa) => getJson(`/demand/baked${q(cbsa)}`);

/** Centroids of an uploaded <tazs> document. Vertex average, NOT the area centroid —
 *  must match run_edit.taz_centroids. Returns [] for anything unreadable. */
export function tazCentroids(tazXml) {
  let doc;
  try {
    doc = new DOMParser().parseFromString(String(tazXml || ''), 'application/xml');
  } catch {
    return [];
  }
  if (!doc || doc.querySelector('parsererror')) return [];
  const out = [];
  for (const el of Array.from(doc.getElementsByTagName('taz'))) {
    const id = (el.getAttribute('id') || '').trim();
    if (!id) continue;
    const points = [];
    for (const token of (el.getAttribute('shape') || '').trim().split(/\s+/)) {
      const parts = token.split(',');
      if (parts.length !== 2) { points.length = 0; break; }
      const lon = Number(parts[0]);
      const lat = Number(parts[1]);
      if (!Number.isFinite(lon) || !Number.isFinite(lat)) { points.length = 0; break; }
      points.push([lon, lat]);
    }
    // Drop a repeated closing vertex; it would drag the average towards itself.
    const first = points[0];
    const last = points[points.length - 1];
    if (points.length > 1 && first[0] === last[0] && first[1] === last[1]) points.pop();
    if (points.length < 3) continue;
    out.push({
      id,
      name: (el.getAttribute('name') || `TAZ_${id}`).trim(),
      lon: points.reduce((s, p) => s + p[0], 0) / points.length,
      lat: points.reduce((s, p) => s + p[1], 0) / points.length,
    });
  }
  return out;
}

/** What the trip-flow layer draws for the demand in force. Counts are EFFECTIVE trips
 *  (× scale), so the layer total equals "Will simulate N trips". For uploaded zones the
 *  total is an upper bound — the service also drops zones containing no road. */
export function buildTripFlowData(baked, demand, scale = 1) {
  const mult = Number.isFinite(scale) && scale >= 1 ? scale : 1;
  const custom = demand?.od?.length ? demand : null;
  const customZones = custom && demand.taz_xml ? tazCentroids(demand.taz_xml) : null;

  const locations = customZones || baked?.locations || [];
  if (!locations.length) return null;

  // od_format decides the id space; no sniffing. Only baked zones carry an H3
  // address, which is why the service refuses taz_xml together with 'h3'.
  const byH3 = !customZones && custom?.od_format === 'h3';
  const index = new window.Map();
  for (const loc of locations) {
    const key = byH3 ? loc.h3 : loc.id;
    if (key != null) index.set(String(key).toLowerCase(), loc);
  }

  const rows = custom
    ? custom.od.map((r) => ({ origin: r.from, dest: r.to, count: r.count }))
    : (baked?.flows || []);

  const flows = [];
  let trips = 0;
  let droppedPairs = 0;
  let droppedTrips = 0;
  for (const row of rows) {
    const from = index.get(String(row.origin).toLowerCase());
    const to = index.get(String(row.dest).toLowerCase());
    const count = (Number(row.count) || 0) * mult;
    if (!from || !to || count <= 0) {
      if (!from || !to) { droppedPairs += 1; droppedTrips += Number(row.count) || 0; }
      continue;
    }
    flows.push({ origin: from.id, dest: to.id, count });
    trips += count;
  }
  if (!flows.length) return null;

  return {
    data: { locations, flows },   // the layer's props; the rest is the caption
    trips,
    pairs: flows.length,
    scale: mult,
    dropped: { pairs: droppedPairs, trips: droppedTrips * mult },
    source: custom ? 'custom' : 'baked',
  };
}

/** Poll a background microsim job until it settles. Stops on every terminal state
 *  INCLUDING failed / unknown / a 404 for an aged-out job. */
export function pollJob(jobId, { onUpdate, intervalMs = 2000, timeoutMs = 900000 } = {}) {
  const started = Date.now();
  let stopped = false;
  const stop = () => { stopped = true; };
  const promise = new Promise((resolve) => {
    const tick = async () => {
      if (stopped) return resolve({ status: 'cancelled' });
      let body;
      try {
        const r = await fetch(`${DEMO_API}/job/${encodeURIComponent(jobId)}`);
        body = await r.json().catch(() => ({}));
        // ANY failing status is terminal, not just 404: other failures answer with no `status`
        // field, and a 401 fires every two seconds while the page is torn down.
        if (!r.ok) body = { status: 'unknown', httpStatus: r.status, ...body };
      } catch {
        body = { status: 'unknown', error: 'Simulation service is not reachable.' };
      }
      if (onUpdate) onUpdate(body);
      if (body.status && !['queued', 'running'].includes(body.status)) return resolve(body);
      if (Date.now() - started > timeoutMs) {
        return resolve({ status: 'failed', error: 'Timed out waiting for the conflict analysis.' });
      }
      setTimeout(tick, intervalMs);
    };
    tick();
  });
  return { promise, stop };
}

// --- Cost -----------------------------------------------------------------
// Cost = elapsed seconds × the published rate of a REFERENCE machine, not of the host
// that answered. Only the marginal cost of one run; idle and fixed infra excluded.
// The rate and estimate table are owned by the edit service (cost.py).

export function fetchCostModel(cbsa) {
  if (!isLiveSimulation()) return Promise.resolve(null);
  return fetch(`${DEMO_API}/cost-model${q(cbsa)}`)
    .then((r) => (r.ok ? r.json() : null))
    .catch(() => null);
}

/** Key into the estimate table: the sorted set of cost-bearing edit kinds. Mirrors
 *  cost.edit_kinds() — a key that does not exist there yields no estimate. */
export function editKindKey(edits, demand) {
  const kinds = [];
  if (edits?.removed?.length || edits?.added?.length
      || (edits?.attrs && Object.keys(edits.attrs).length)) kinds.push('network');
  // A policy is one kind, not the edits the service derives from it.
  if (edits?.policy?.kind === 'school_zone') kinds.push('school_zone');
  if (edits?.signal?.phases || edits?.signal?.program) kinds.push('signal');
  if (edits?.zone) kinds.push('zone');
  // ANY non-empty demand, which is what the service tests (`if demand:`). Enumerating
  // od / fleet / taz_xml misses a pure-multiplier demand.
  if (demand && Object.keys(demand).length > 0) kinds.push('demand');
  return kinds.sort().join('|');
}

/** Predicted {seconds, usd, samples} for a staged edit set, or null. */
export function estimateCost(model, edits, demand) {
  if (!model?.estimates) return null;
  return model.estimates[editKindKey(edits, demand)] || null;
}

/** A run has two bills: the macro chain, and a conflict microsim that outlives the
 *  answer. Both blocks come from cost.block() at the same reference rate, so adding
 *  them is addition, not a second model. Returns the macro block untouched when there
 *  is no job half (no job, skipped, or failed). */
export function combineCost(macro, job) {
  if (!macro) return job || null;
  if (!job) return macro;
  return {
    ...macro,
    elapsed_s: round1((macro.elapsed_s || 0) + (job.elapsed_s || 0)),
    usd: round6((macro.usd || 0) + (job.usd || 0)),
    // Step names are unique across the two halves (the macro chain never runs a
    // microsim step and vice versa), so this is a concatenation, not a merge
    // that could drop a measurement.
    steps: { ...(macro.steps || {}), ...(job.steps || {}) },
    cached: combineCached(macro.cached, job.cached),
  };
}

function combineCached(a, b) {
  if (!a) return b || null;
  if (!b) return a;
  return {
    steps: [...(a.steps || []), ...(b.steps || [])],
    saved_s: round1((a.saved_s || 0) + (b.saved_s || 0)),
    saved_usd: round6((a.saved_usd || 0) + (b.saved_usd || 0)),
  };
}

const round1 = (n) => Math.round(n * 10) / 10;
const round6 = (n) => Math.round(n * 1e6) / 1e6;

/** What a finished run cost. `cost_total` is written on when the background job
 *  reports (trackSafetyJob); `cost` is the macro block and is never overwritten. */
export const runCost = (result) => result?.cost_total || result?.cost || null;

/** A run costs fractions of a cent, so two decimals would render everything "$0.00". */
export function formatUsd(usd) {
  if (usd == null) return null;
  if (usd >= 1) return `$${usd.toFixed(2)}`;
  if (usd >= 0.01) return `$${usd.toFixed(3)}`;
  return `$${usd.toFixed(4)}`;
}

// Attach a scenario's per-edge metrics onto each edge feature's properties as
// `metric`, so a single line layer can color by V/C. Removed edges (in the edit
// record) are flagged so the layer can grey/hide them. Added edges (which don't
// exist in edges.geojson) are returned separately as their own features.
export function applyScenario(edgesGeojson, metrics, edits) {
  const removed = new Set(edits?.removed_edges || edits?.edges || []);
  const metricByEdge = metrics?.edges || {};

  const features = edgesGeojson.features.map((f) => {
    const m = metricByEdge[f.properties.id] || null;
    return {
      ...f,
      properties: {
        ...f.properties,
        metric: m,
        removed: removed.has(f.properties.id),
      },
    };
  });

  const added = (edits?.added_edges || []).map((a) => ({
    type: 'Feature',
    geometry: { type: 'LineString', coordinates: a.coordinates },
    properties: {
      id: a.id, name: a.name, lanes: a.lanes, speed_kmh: a.speed_kmh,
      length_m: a.length_m, added: true,
      metric: metricByEdge[a.id] || null,
    },
  }));

  return { features, added };
}

export { DEMO_API };
